import type { ApMountType, FloorGeometry, FloorObstacle } from './types'
import { resolveObstacleFootprint } from './obstacles'
import { distanceToNearestWall } from './hitTest'

export type FloorPlacementBounds = {
  xMin: number
  xMax: number
  yMin: number
  yMax: number
}

const DEFAULT_MARGIN = 3
/** Inset for ceiling/desk APs — keeps them off perimeter walls and corners. */
export const AP_EDGE_MARGIN = 6
/** Extra corner exclusion when near two floor edges at once. */
export const AP_CORNER_MARGIN = 10
/** Min distance from wall centerlines for non-wall-mounted APs (% floor coords). */
export const AP_WALL_CLEARANCE_PCT = 4
/** Walls and rooms trace the floor image — full 0–100% canvas. */
export const GEOMETRY_EDGE_MARGIN = 0

/**
 * Usable floor area for AP placement and RF metrics (percent coords).
 * Crop is applied visually via the crop dialog — coords always map to the displayed floor box.
 */
export function floorPlacementBounds(
  _geometry: FloorGeometry,
  margin = DEFAULT_MARGIN,
  _hasBackgroundImage = true,
): FloorPlacementBounds {
  return { xMin: margin, xMax: 100 - margin, yMin: margin, yMax: 100 - margin }
}

export function apPlacementMargin(mount: ApMountType): number {
  if (mount === 'wall') return DEFAULT_MARGIN
  if (mount === 'outdoor') return 2
  return AP_EDGE_MARGIN
}

export function apPlacementBounds(geometry: FloorGeometry, mount: ApMountType): FloorPlacementBounds {
  return floorPlacementBounds(geometry, apPlacementMargin(mount))
}

export function nearApFloorCorner(
  x: number,
  y: number,
  bounds: FloorPlacementBounds,
  inset = AP_CORNER_MARGIN,
): boolean {
  const nearX = x <= bounds.xMin + inset || x >= bounds.xMax - inset
  const nearY = y <= bounds.yMin + inset || y >= bounds.yMax - inset
  return nearX && nearY
}

export function clampToFloorBounds(
  x: number,
  y: number,
  geometry: FloorGeometry,
  hasBackgroundImage = true,
): { x: number; y: number } {
  const b = floorPlacementBounds(geometry, DEFAULT_MARGIN, hasBackgroundImage)
  return {
    x: Math.min(b.xMax, Math.max(b.xMin, x)),
    y: Math.min(b.yMax, Math.max(b.yMin, y)),
  }
}

export function clampApPosition(
  x: number,
  y: number,
  mount: ApMountType,
  geometry: FloorGeometry,
): { x: number; y: number } {
  const b = apPlacementBounds(geometry, mount)
  return {
    x: Math.min(b.xMax, Math.max(b.xMin, x)),
    y: Math.min(b.yMax, Math.max(b.yMin, y)),
  }
}

/** Ceiling/desk APs should not hug perimeter walls or floor corners. */
export function isServiceableCeilingApSite(
  x: number,
  y: number,
  geometry: FloorGeometry,
  mount: ApMountType,
): boolean {
  if (mount === 'wall' || mount === 'outdoor') return true
  const bounds = apPlacementBounds(geometry, mount)
  if (x < bounds.xMin || x > bounds.xMax || y < bounds.yMin || y > bounds.yMax) return false
  if (nearApFloorCorner(x, y, bounds)) return false
  if (geometry.walls.length > 0 && distanceToNearestWall(x, y, geometry.walls) < AP_WALL_CLEARANCE_PCT) {
    return false
  }
  return true
}

/** Obstacle (x,y) is center — keep full footprint inside floor bounds. */
export function clampObstacleCenter(
  x: number,
  y: number,
  obstacle: Pick<FloorObstacle, 'type' | 'w' | 'h' | 'widthM' | 'depthM'>,
  floorWidthM: number,
  floorHeightM: number,
  geometry: FloorGeometry,
  margin = DEFAULT_MARGIN,
): { x: number; y: number } {
  const { w, h } = resolveObstacleFootprint(obstacle, floorWidthM, floorHeightM)
  const b = floorPlacementBounds(geometry, margin)
  const halfW = w / 2
  const halfH = h / 2
  const xMin = Math.min(b.xMax, b.xMin + halfW)
  const xMax = Math.max(b.xMin, b.xMax - halfW)
  const yMin = Math.min(b.yMax, b.yMin + halfH)
  const yMax = Math.max(b.yMin, b.yMax - halfH)
  return {
    x: Math.min(xMax, Math.max(xMin, x)),
    y: Math.min(yMax, Math.max(yMin, y)),
  }
}

/** Move a wall segment as a rigid body; keep the whole segment inside the floor canvas. */
export function clampWallSegmentTranslate(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  geometry: FloorGeometry,
  margin = GEOMETRY_EDGE_MARGIN,
): { x1: number; y1: number; x2: number; y2: number } {
  const b = floorPlacementBounds(geometry, margin)
  let dx = 0
  let dy = 0
  const minX = Math.min(x1, x2)
  const maxX = Math.max(x1, x2)
  const minY = Math.min(y1, y2)
  const maxY = Math.max(y1, y2)
  if (minX < b.xMin) dx = b.xMin - minX
  if (maxX + dx > b.xMax) dx = b.xMax - maxX
  if (minY < b.yMin) dy = b.yMin - minY
  if (maxY + dy > b.yMax) dy = b.yMax - maxY
  return { x1: x1 + dx, y1: y1 + dy, x2: x2 + dx, y2: y2 + dy }
}

export function pointInFloorBounds(
  x: number,
  y: number,
  geometry: FloorGeometry,
  hasBackgroundImage = true,
  extraMargin = 0,
): boolean {
  const b = floorPlacementBounds(geometry, DEFAULT_MARGIN + extraMargin, hasBackgroundImage)
  return x >= b.xMin && x <= b.xMax && y >= b.yMin && y <= b.yMax
}
